import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createDealsClient } from './api';
import type {
  DealIntegrationLookupFilter,
  DealLookupFilter,
  DealMutationPayload,
  DealsFilter,
} from './model';

export const dealsQueryKey = ['demand', 'deals'] as const;
export const dealsListsQueryKey = [...dealsQueryKey, 'list'] as const;
export const dealStatusCountsQueryKey = [...dealsQueryKey, 'status-counts'] as const;

function useDealsClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return useMemo(
    () =>
      createDealsClient({
        config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
        accessToken: session.accessToken,
      }),
    [config.apiBaseUrl, config.networkId, session.accessToken],
  );
}

export function useDealsQuery(filter: DealsFilter, enabled = true) {
  const client = useDealsClient();
  return useQuery({
    queryKey: [...dealsListsQueryKey, filter],
    queryFn: () => client.list(filter),
    enabled,
  });
}

export function useDealStatusCountsQuery(searchTerm: string, enabled = true) {
  const client = useDealsClient();
  return useQuery({
    queryKey: [...dealStatusCountsQueryKey, searchTerm],
    queryFn: () => client.getStatusCounts(searchTerm),
    enabled,
  });
}

export function useDealQuery(id: string | undefined) {
  const client = useDealsClient();
  return useQuery({
    queryKey: [...dealsQueryKey, 'detail', id],
    queryFn: () => client.get(id ?? ''),
    enabled: Boolean(id),
    refetchOnMount: 'always',
  });
}

export function useDealTargetingsQuery(filter: DealLookupFilter, enabled = true) {
  const client = useDealsClient();
  return useQuery({
    queryKey: [...dealsQueryKey, 'lookup', 'targetings', filter],
    queryFn: () => client.searchTargetings(filter),
    enabled,
  });
}

export function useDealIntegrationsQuery(filter: DealIntegrationLookupFilter, enabled = true) {
  const client = useDealsClient();
  return useQuery({
    queryKey: [...dealsQueryKey, 'lookup', 'integrations', filter],
    queryFn: () => client.searchIntegrations(filter),
    enabled,
  });
}

export function useDealMutations() {
  const client = useDealsClient();
  const queryClient = useQueryClient();
  const invalidateListsAndCounts = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: dealsListsQueryKey }),
      queryClient.invalidateQueries({ queryKey: dealStatusCountsQueryKey }),
    ]);
  return {
    create: useMutation({ mutationFn: client.create, onSuccess: invalidateListsAndCounts }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: DealMutationPayload }) =>
        client.update(id, payload),
      onSuccess: invalidateListsAndCounts,
    }),
  };
}
